<template>
  <v-expansion-panels v-model="expandedPanels" multiple>
    <!-- URL Panel – shows all URLs for products -->
    <v-expansion-panel>
      <v-expansion-panel-title>
        Product URLs ({{ urlFilters.displayedUrls.value.length }})
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col cols="12">
            <!-- Use the common URL filter chips including sitemap filters -->
            <UrlFilterChips
              :fetch-status-counts="urlFilters.fetchStatusCounts.value"
              :active-fetch-status-filters="urlFilters.activeFetchStatusFilters.value"
              :toggle-fetch-status-filter="urlFilters.toggleFetchStatusFilter"
              show-sitemap-filters
              :sitemap-count="urlFilters.sitemapCount.value"
              :non-sitemap-count="urlFilters.nonSitemapCount.value"
              :active-sitemap-filters="urlFilters.activeSitemapFilters.value"
              :toggle-sitemap-filter="urlFilters.toggleSitemapFilter"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="url in urlFilters.displayedUrls.value"
            :key="url.pathname"
            cols="12"
            sm="6"
            md="4"
          >
            <UrlCard :item="url" />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>

    <!-- Processed Products Panel -->
    <v-expansion-panel v-if="filteredProducts.length > 0">
      <v-expansion-panel-title>
        Processed Products ({{ filteredProducts.length }})
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-row>
          <v-col cols="12">
            <div class="d-flex flex-wrap gap-2 mb-3">
              <v-chip
                v-if="productsStore.missingNameCount > 0"
                :color="activeFilters.includes('name') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('name')"
              >
                Missing Name ({{ productsStore.missingNameCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingMetaTitleCount > 0"
                :color="activeFilters.includes('metaTitle') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('metaTitle')"
              >
                Missing Meta Title ({{ productsStore.missingMetaTitleCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingMetaDescriptionCount > 0"
                :color="activeFilters.includes('metaDescription') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('metaDescription')"
              >
                Missing Meta Description ({{ productsStore.missingMetaDescriptionCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingImagesCount > 0"
                :color="activeFilters.includes('images') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('images')"
              >
                Missing Images ({{ productsStore.missingImagesCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingDescriptionCount > 0"
                :color="activeFilters.includes('description') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('description')"
              >
                Missing Description ({{ productsStore.missingDescriptionCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingPriceCount > 0"
                :color="activeFilters.includes('price') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('price')"
              >
                Missing Price ({{ productsStore.missingPriceCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingSpecificationCount > 0"
                :color="activeFilters.includes('specification') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('specification')"
              >
                Missing Specification ({{ productsStore.missingSpecificationCount }})
              </v-chip>
              <v-chip
                v-if="productsStore.missingMainCategoryCount > 0"
                :color="activeFilters.includes('mainCategory') ? 'primary' : 'grey lighten-1'"
                outlined
                clickable
                @click="toggleFilter('mainCategory')"
              >
                Missing Main Category ({{ productsStore.missingMainCategoryCount }})
              </v-chip>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="product in filteredProducts"
            :key="product.pathname"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <ProductItem :product="product" />
          </v-col>
        </v-row>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useProductsStore } from '@/store/productsStore';
import UrlCard from './UrlCard.vue';
import ProductItem from './ProductItem.vue';
import UrlTypes from '@/enums/UrlTypes';
import { useActiveFilters } from '@/composables/useActiveFilters';
import { applyMissingFilters } from '@/helpers/filterMissing';
import UrlFilterChips from '@/components/UrlFilterChips.vue';
import { useUrlFilters } from '@/composables/useUrlFilters';

const productsStore = useProductsStore();
const { activeFilters, toggleFilter } = useActiveFilters();

const filteredProducts = computed(() =>
  applyMissingFilters(productsStore.products, activeFilters.value, (product, filter) => {
    switch (filter) {
      case 'name':
        return !product.name;
      case 'metaTitle':
        return !product.metaTitle;
      case 'metaDescription':
        return !product.metaDescription;
      case 'images':
        return !(product.images && product.images.length > 0);
      case 'description':
        return !(product.description && product.description.descriptionHtml);
      case 'price':
        return !(product.price && product.price.price);
      case 'specification':
        return !(
          product.specification &&
          (product.specification.suitableFor ||
           product.specification.size ||
           product.specification.requiredSpace)
        );
      case 'mainCategory':
        return !product.mainCategory;
      default:
        return true;
    }
  })
);

// Use the new composable for URL filtering.
const urlFilters = useUrlFilters((url) => url.urlType === UrlTypes.PRODUCT);

// Expand processed panel by default (panel index 1)
const expandedPanels = ref([1]);
</script>
